import type { FormValues, FormValuesEvent } from "@/widgets/classic-form/form";
import { createEventDefaultInstance } from "@/widgets/classic-form/form";
import { isEventInstanceEmpty } from "@/entities/data-model/eventInstance";
import { mapEventInstanceWithExactness } from "./mapEventInstanceWithExactness";

export interface SeedInstanceSlotsProps {
    formValuesEventsData: FormValues["events"];
    eventKey: string;
    dataModelEvent?: FormValuesEvent;
    rowCount: number;
    isYearExactnessEnabled?: boolean;
    isPlaceExactnessEnabled?: boolean;
    matchAllTermsExactly: boolean;
    isEditForm: boolean;
    defaultYearProximity: number | null;
}

/**
 * Seeds one event entry slot by slot, up to `rowCount` rows.
 *
 * Deliberately not "skip the entry if it has any instances" (the old first-writer-wins guard):
 * several fieldsets can contribute to the same entry, so each non-empty slot is left alone while empty/missing slots are (re)seeded.
 */
export const seedInstanceSlots = ({
    formValuesEventsData,
    eventKey,
    dataModelEvent,
    rowCount,
    matchAllTermsExactly,
    isEditForm,
    isYearExactnessEnabled = true,
    isPlaceExactnessEnabled = true,
    defaultYearProximity,
}: SeedInstanceSlotsProps) => {
    const entry = formValuesEventsData[eventKey];
    if (!entry) return;

    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        const existing = entry.instances[rowIndex];
        if (existing && !isEventInstanceEmpty(existing)) continue;

        const yearProximity = isYearExactnessEnabled ? defaultYearProximity : null;
        const instance = dataModelEvent?.instances[rowIndex] ?? createEventDefaultInstance(yearProximity);

        entry.instances[rowIndex] = mapEventInstanceWithExactness({
            instance,
            isYearExactnessEnabled,
            isPlaceExactnessEnabled,
            matchAllTermsExactly,
            isEditForm,
            defaultYearProximity,
        });
    }
};
